import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { DialogoCambiosSinGuardar } from "@/components/DialogoCambiosSinGuardar";
import {
  InformeFormulario,
  type InformeFormularioHandle,
} from "@/components/informes/InformeFormulario";
import { AdjuntoDelAvaluoFiscal } from "@/components/informes/AdjuntoDelAvaluoFiscal";
import {
  ImportadorDeTasacion,
  motivoDeRechazoDeLaTasacion,
  type OrigenDeLaTasacion,
} from "@/components/informes/ImportadorDeTasacion";
import { Skeleton } from "@/components/ui/skeleton";
import { useGuardiaDeCambios } from "@/hooks/useGuardiaDeCambios";
import { useCatalogosDelInforme, useCrearInforme } from "@/hooks/useInformes";
import {
  adjuntarArchivoDeAntecedenteRequest,
  CODIGO_CERTIFICADO_AVALUO_SII,
  CODIGO_TASACION_CONSULTEK,
  importarTasacionRequest,
  resolverImportacionRequest,
  subirImagenDelInformeRequest,
  type PropuestaDeTasacion,
} from "@/lib/informes";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";

/**
 * Cuántos datos trajo la propuesta, en una línea.
 *
 * DICE LO QUE ENCONTRÓ Y NO "listo", porque lo normal es que falte casi todo: una
 * cotización de Consultek trae los roles, la comuna y la superficie, y no trae
 * dirección ni descripciones. Un informe de tasación completo trae bastante más,
 * pero sigue sin traer dirección postal si el predio es rural. El cliente nunca
 * se propone. Un mensaje de éxito a secas haría creer que el formulario quedó
 * completo.
 */
function resumirPropuesta(propuesta: PropuestaDeTasacion): string {
  const campos = [
    propuesta.propiedad_nombre,
    propuesta.direccion,
    propuesta.descripcion_ubicacion,
    propuesta.descripcion,
    // La unidad CUENTA por lo mismo que el uso de abajo, y es la que más
    // conviene que la persona vaya a mirar: cambia el significado de todas las
    // superficies de la tabla sin cambiar ni uno de sus números.
    propuesta.unidad_superficie,
    // El cliente, el tipo de tasación (la plantilla) y el formato de salida no
    // se proponen: los elige o los escribe la persona.
  ].filter((valor) => valor !== null).length;

  // Cuenta ROLES: en el árbol plano cada uno ES una fila del documento — ya
  // no hay un nivel "propiedad" bajo el que se agrupen dos roles.
  const roles = propuesta.roles.length;

  if (campos === 0 && roles === 0) {
    return "No encontramos datos de la propiedad en este documento. Complétalos a mano; el archivo queda adjunto igual.";
  }

  const partes = [
    campos === 1 ? "1 campo" : `${campos} campos`,
    roles === 1 ? "1 rol" : `${roles} roles`,
  ];

  return `Precargamos ${partes.join(" y ")}. Revísalos antes de crear el informe.`;
}

/**
 * Creación del informe de tasación (Módulo E, pantalla `15 UN`).
 *
 * Es el punto de partida del proceso: lo que se captura acá es la base de la
 * cotización que el cliente aprueba después. El informe queda en estado
 * "Creado" y aparece en Informes en proceso, donde se definen los antecedentes
 * requeridos y se cotiza.
 */
export function CrearInforme() {
  const navigate = useNavigate();
  const catalogos = useCatalogosDelInforme();
  const crear = useCrearInforme();

  /* Aviso de cambios sin guardar (regla transversal, spec §13). Acá el aviso NO
     ofrece "Guardar y salir": el alta del API fija `estado = creado` y no acepta
     un informe a medio llenar, así que el "guardar como Incompleto" que pide
     RF-E1 §8.3 no tiene todavía dónde apoyarse. Ofrecerlo significaría crear un
     informe COMPLETO con datos a medias, que es peor que perder el borrador. */
  const [hayCambios, setHayCambios] = useState(false);
  const registrarCambios = useCallback((hay: boolean) => setHayCambios(hay), []);

  /* La imagen satelital elegida, ÚNICA por informe, retenida hasta que el
     informe exista. `POST /informes/{id}/imagen` necesita un id que acá
     todavía no hay: lo asigna el alta. Vive en esta pantalla y no dentro del
     formulario porque es quien la va a subir en cuanto lo tenga. */
  const [imagen, setImagen] = useState<File | null>(null);

  const registrarImagen = useCallback((archivo: File | null) => {
    setImagen(archivo);
  }, []);

  const hayImagen = imagen !== null;

  /* --- Importar una tasación anterior -------------------------------------
     TODO ESTO VIVE SOLO ACÁ, en la creación. Es una ayuda de CARGA: sirve
     mientras el formulario está en blanco. Una vez creado el informe los datos
     ya están cargados y revisados, así que la pantalla de edición usa el MISMO
     formulario sin pasarle el importador — ahí lo que queda es adjuntar desde
     la pestaña de antecedentes históricos, que es registro y no ayuda. */
  const formRef = useRef<InformeFormularioHandle>(null);

  /* El origen se declara ANTES de subir y no se deduce del archivo: son PDFs de
     empresas distintas y nada dentro los distingue. Sin declararlo, el documento
     quedaría adjunto en la categoría equivocada. */
  const [origen, setOrigen] = useState<OrigenDeLaTasacion>(CODIGO_TASACION_CONSULTEK);

  /* El PDF, retenido hasta que el informe exista: `POST /informes/{id}/…`
     necesita un id que acá todavía no hay. Es el mismo camino que la imagen
     satelital. */
  const [tasacion, setTasacion] = useState<File | null>(null);
  /* El certificado de avalúo del SII NO es una tasación: no se lee con IA ni
     toca `ejecucionId`. Es un adjunto a secas, retenido igual que la tasación
     hasta que el informe tenga id. */
  const [avaluo, setAvaluo] = useState<File | null>(null);
  const [leyendo, setLeyendo] = useState(false);
  const [resumenDeImportacion, setResumenDeImportacion] = useState<string | null>(null);
  const [errorDeImportacion, setErrorDeImportacion] = useState<string | null>(null);

  /* La corrida de IA queda PENDIENTE hasta que se sabe qué hizo la persona: se
     marca aceptada al crear el informe y descartada al quitar el documento. Es
     la única medida de si la función sirve. */
  const [ejecucionId, setEjecucionId] = useState<number | null>(null);

  async function leerTasacion(archivo: File) {
    const motivo = motivoDeRechazoDeLaTasacion(archivo);

    if (motivo !== null) {
      setErrorDeImportacion(motivo);
      return;
    }

    // El archivo se retiene ANTES de leerlo, y se queda aunque la lectura
    // falle: sirve como antecedente histórico igual. Perderlo por un proveedor
    // caído obligaría a subirlo dos veces.
    setTasacion(archivo);
    formRef.current?.marcarTasacionAnterior();
    setErrorDeImportacion(null);
    setResumenDeImportacion(null);
    setLeyendo(true);

    try {
      const { ejecucion_id, propuesta } = await importarTasacionRequest(archivo);

      formRef.current?.precargar(propuesta);
      setEjecucionId(ejecucion_id);
      setResumenDeImportacion(resumirPropuesta(propuesta));
    } catch (error) {
      setErrorDeImportacion(
        getApiErrorMessage(
          error,
          "No pudimos leer el documento. Completa el formulario a mano; el archivo queda adjunto igual.",
        ),
      );
    } finally {
      setLeyendo(false);
    }
  }

  function quitarTasacion() {
    // Quitar el documento es descartar la propuesta. Si falla el registro no se
    // avisa: es auditoría, y molestar a alguien que solo quitó un archivo con
    // un error que no puede resolver no ayuda a nadie.
    if (ejecucionId !== null) {
      void resolverImportacionRequest(ejecucionId, "descartado").catch(() => {});
    }

    setTasacion(null);
    setEjecucionId(null);
    setResumenDeImportacion(null);
    setErrorDeImportacion(null);
  }

  /* Elegir una imagen, una tasación o el avalúo ES un cambio sin guardar, y por eso la
     guardia mira todo eso: `formState.isDirty` no puede ver los archivos
     —no son campos de react-hook-form—, así que sin esto alguien que solo soltó
     un documento podría salir de la pantalla y perderlo sin que nada le avise. */
  const guardia = useGuardiaDeCambios(hayCambios || hayImagen || tasacion !== null || avaluo !== null);

  return (
    <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <p className="text-ink-muted max-w-[40rem] text-sm">
        Completa los datos y selecciona los contenidos que incluirá el informe.
      </p>

      {catalogos.isPending ? <CatalogosCargando /> : null}

      {catalogos.isError ? (
        <p className="text-destructive text-sm">
          No pudimos cargar los catálogos del formulario. Recarga la página para reintentar.
        </p>
      ) : null}

      {catalogos.data ? (
        <InformeFormulario
          ref={formRef}
          catalogos={catalogos.data}
          enviando={crear.isPending}
          // Mientras se lee el documento no se puede crear el informe: hacerlo
          // descartaría en silencio los datos que la persona está esperando, y
          // la propuesta llegaría a una pantalla que ya navegó.
          bloqueado={leyendo}
          onCambios={registrarCambios}
          imagenPendiente={imagen}
          onImagenPendiente={registrarImagen}
          // SOLO ACÁ. La edición usa este mismo formulario y no lo pasa: ahí los
          // datos ya están cargados y ofrecer el import sería ofrecer pisarlos.
          importador={
            <>
              <ImportadorDeTasacion
                origen={origen}
                archivo={tasacion}
                leyendo={leyendo}
                resumen={resumenDeImportacion}
                error={errorDeImportacion}
                onOrigen={setOrigen}
                onArchivo={(archivo) => void leerTasacion(archivo)}
                onQuitar={quitarTasacion}
              />
              <AdjuntoDelAvaluoFiscal
                archivo={avaluo}
                onArchivo={setAvaluo}
                onQuitar={() => setAvaluo(null)}
              />
            </>
          }
          onEnviar={(_valores, input, { errorDeCampo }) =>
            crear.mutate(input, {
              onSuccess: async (informe) => {
                // LA IMAGEN VIAJA ACÁ, en una llamada aparte, y no dentro del
                // alta: `POST /informes` manda JSON con roles, contenidos y
                // responsables anidados, y meterle un archivo obligaría a
                // convertirlo en multipart —donde los arreglos anidados se
                // codifican a mano—. Recién ahora existe el id al que subirla.
                //
                // Si esta llamada falla, el informe YA está creado: se avisa y
                // se sigue. Tratarla como un fracaso del alta dejaría a la
                // persona creyendo que tiene que empezar de nuevo, y crearía
                // un informe duplicado cuando lo intentara.
                if (imagen !== null) {
                  try {
                    await subirImagenDelInformeRequest(informe.id, imagen);
                  } catch {
                    toast.warning(
                      "El informe se creó, pero no pudimos guardar la imagen. Vuelve a subirla desde el informe.",
                    );
                  }
                }

                // LA TASACIÓN IMPORTADA SE ADJUNTA ACÁ, por lo mismo que la
                // imagen: el endpoint necesita el id del informe, que recién
                // ahora existe. Queda en la categoría que la persona DECLARÓ al
                // subirla, no en una deducida del archivo.
                //
                // Se adjunta aunque la lectura haya fallado: el documento sirve
                // como antecedente histórico independientemente de que la IA
                // haya podido sacarle datos.
                if (tasacion !== null) {
                  const documento = (catalogos.data?.documentos.historico ?? []).find(
                    (tipo) => tipo.codigo === origen,
                  );

                  try {
                    if (documento === undefined) {
                      throw new Error("El catálogo no trae el documento de tasaciones anteriores.");
                    }
                    await adjuntarArchivoDeAntecedenteRequest(informe.id, documento.id, tasacion);
                  } catch {
                    toast.warning(
                      "El informe se creó, pero no pudimos adjuntar la tasación anterior. Vuelve a subirla desde Antecedentes históricos.",
                    );
                  }
                }

                // EL CERTIFICADO DE AVALÚO VA AL TIPO LEGAL por su código, nunca
                // por nombre. Si falla o el catálogo no lo trae, el informe ya
                // existe: se avisa y se sigue, como con la imagen.
                if (avaluo !== null) {
                  const documento = (catalogos.data?.documentos.legal ?? []).find(
                    (tipo) => tipo.codigo === CODIGO_CERTIFICADO_AVALUO_SII,
                  );

                  try {
                    if (documento === undefined) {
                      throw new Error("El catálogo no trae el certificado de avalúo.");
                    }
                    await adjuntarArchivoDeAntecedenteRequest(informe.id, documento.id, avaluo);
                  } catch {
                    toast.warning(
                      "El informe se creó, pero no pudimos adjuntar el certificado de avalúo. Vuelve a subirlo desde Antecedentes legales.",
                    );
                  }
                }

                // La propuesta terminó ACEPTADA: la persona creó el informe con
                // ella a la vista. Si el registro falla no se avisa —es
                // auditoría, y el informe ya está creado—.
                if (ejecucionId !== null) {
                  void resolverImportacionRequest(ejecucionId, "aceptado").catch(() => {});
                }

                toast.success(`Informe de ${informe.propiedad_nombre} creado.`);
                // Antes de navegar: el formulario sigue "sucio" en este tick y
                // la guardia frenaría la navegación que corona su propio éxito.
                // Los archivos también se sueltan, por el mismo motivo.
                setImagen(null);
                setTasacion(null);
                setAvaluo(null);
                guardia.desactivar();
                // Al EXPEDIENTE recién creado, no al hub: crear es el paso 1
                // de un proceso que sigue en la definición de antecedentes y
                // termina en la cotización. Mandar al inicio cortaba el flujo
                // y dejaba el informe sin una vía obvia de vuelta.
                navigate(`/informes/${informe.id}`);
              },
              onError: (error) => {
                const errores = parseValidationErrors(error);

                if (errores) {
                  // El 422 nombra TODOS los campos que fallaron: se marcan
                  // todos, no solo el primero, para que el usuario no los
                  // descubra de a uno.
                  Object.entries(errores).forEach(([campo, mensaje]) =>
                    errorDeCampo(campo, mensaje),
                  );
                  toast.error("Revisa los campos marcados.");
                  return;
                }

                toast.error(
                  getApiErrorMessage(error, "No pudimos crear el informe. Inténtalo de nuevo."),
                );
              },
            })
          }
        />
      ) : null}

      <DialogoCambiosSinGuardar
        abierto={guardia.bloqueado}
        descripcion="Este informe todavía no se ha creado. Si sales ahora se perderán los datos que completaste."
        onSalirSinGuardar={guardia.continuar}
        onCancelar={guardia.cancelar}
      />
    </div>
  );
}

/**
 * Esqueleto de carga con la forma del formulario, no un spinner suelto: la
 * pantalla es larga y un spinner centrado hace saltar todo el layout cuando
 * llegan los datos.
 */
function CatalogosCargando() {
  return (
    <div className="space-y-6" aria-hidden="true">
      <div className="bg-card space-y-5 rounded-xl border p-6">
        <div className="grid gap-5 md:grid-cols-2">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
        <Skeleton className="h-24" />
        <div className="grid gap-5 md:grid-cols-2">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      </div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );
}
